import { useEffect } from 'react';
import { useReceiptStore } from '../../store/receipt.js';

const TOAST_AUTO_DISMISS_MS = 5000;

/**
 * Small floating success card mounted once at the app shell.
 *
 * Behavior:
 *   - Becomes visible when `pushAndPrint` flips `showToast` to true.
 *   - Auto-dismisses 5 seconds later.
 *   - "Print again" calls `printAgain` (does NOT dismiss the toast — the
 *     cashier may want to print a third copy without the toast disappearing
 *     between clicks; the timer is not reset by reprint).
 *   - The "×" affordance is always keyboard-focusable.
 *
 * Why a toast (rather than success state inside PaymentModal):
 *   - PaymentModal closes on charge success; embedding the reprint button
 *     there would require keeping the modal open. Cashiers want to start the
 *     next sale immediately; the toast lets them do that while the option to
 *     reprint stays at the edge of the screen.
 *   - This component lives outside the modal subtree and survives modal
 *     dismount, so reprint works even if the PaymentModal has been disposed.
 *
 * Styling:
 *   - Inline styles only — no edits to `pos.css` are required to install.
 *     The cashier app's CSS conventions can absorb this later by moving the
 *     declarations into `pos.css` (see README, "Phase 5b polish").
 *   - Position fixed bottom-right (top-right would collide with the existing
 *     header CTAs from Phase 4f).
 */
export function RecentReceiptToast(): JSX.Element | null {
  const showToast = useReceiptStore((s) => s.showToast);
  const printing = useReceiptStore((s) => s.printing);
  const printBlocked = useReceiptStore((s) => s.printBlocked);
  const printError = useReceiptStore((s) => s.printError);
  const lastReceipt = useReceiptStore((s) => s.lastReceipt);
  const printAgain = useReceiptStore((s) => s.printAgain);
  const dismissToast = useReceiptStore((s) => s.dismissToast);

  // Both failure modes hold the toast open and turn it into a warning card:
  // a sale whose receipt never came out must not scroll away on a 5s timer.
  const failed = printBlocked || printError !== null;

  useEffect(() => {
    // Keep the toast up when the print failed so the cashier can act on it
    // (allow pop-ups / tap Print again). Otherwise auto-dismiss.
    if (!showToast || failed) return;
    const handle = window.setTimeout(dismissToast, TOAST_AUTO_DISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [showToast, failed, dismissToast]);

  if (!showToast || !lastReceipt) return null;

  const number = lastReceipt.orderCode ?? lastReceipt.orderId;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '16px',
        right: '16px',
        zIndex: 9999,
        background: failed ? '#7c2d12' : '#0f172a',
        color: '#f8fafc',
        padding: '10px 12px',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        maxWidth: '320px',
        fontSize: '13px',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '16px' }}>{failed ? '⚠️' : '✓'}</span>
      <span style={{ flex: 1 }}>
        {printBlocked
          ? `Receipt #${number}: pop-up blocked — tap Print again (or allow pop-ups)`
          : printError !== null
            ? `Receipt #${number}: ${printError}`
            : `Receipt #${number} printed`}
      </span>
      <button
        type="button"
        onClick={() => {
          // Fire-and-forget: errors are surfaced via the store's printing flag
          // resetting; we don't propagate to the user here.
          void printAgain();
        }}
        disabled={printing}
        style={{
          background: '#1e40af',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          padding: '6px 10px',
          cursor: printing ? 'wait' : 'pointer',
          fontSize: '12px',
          opacity: printing ? 0.6 : 1,
        }}
      >
        Print again
      </button>
      <button
        type="button"
        onClick={dismissToast}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          color: '#94a3b8',
          border: 'none',
          padding: '0 4px',
          cursor: 'pointer',
          fontSize: '16px',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
